/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'
import { Header, HeaderGroup } from '@tanstack/react-table'
import TableHeadRow from 'components/Ui/Tanstack/TableHeadRow'
import TableHeadColumn from 'components/Ui/Tanstack/TableHeadColumn'
import TableHeadColumnInnerWrapper from 'components/Ui/Tanstack/TableHeadColumnInnerWrapper'
import TableHeadColumnTitle from 'components/Ui/Tanstack/TableHeadColumnTitle'
import UiTableColumnSearch from 'components/Ui/UiTableColumnSearch'
import { TableInitBase } from 'utils/hooks/useTable'
import { getTableColumnClassName, resolveTableColumnWidthKey, TableColumnMeta } from 'utils/table/tableColumnWidths'

interface UiTanstackTableHeaderRowProps<TData> {
    headerGroup: HeaderGroup<TData>
    tableInit: TableInitBase
    filterableHeaderIds: string[]
    renderHeaderFilter?: (header: Header<TData, unknown>, tableInit: TableInitBase) => React.ReactNode
    renderHeaderTitleProps?: (header: Header<TData, unknown>, tableInit: TableInitBase) => Record<string, unknown>
    searchVisibility: Record<string, boolean>
    onToggleSearch: (headerId: string) => void
    translate: (value: string) => string
    isBatchMode?: boolean
    renderBatchHeaderControl?: (header: Header<TData, unknown>, tableInit: TableInitBase) => React.ReactNode
}

const UiTanstackTableHeaderRow = <TData,>(props: UiTanstackTableHeaderRowProps<TData>) => {
    return (
        <TableHeadRow className="UiTanstackTableHeaderRow">
            {props.headerGroup.headers.map((header) => {
                // const columnFiltersVisible = props.tableInit.columnFiltersVisible ?? true
                const columnFiltersVisible = true
                const isFilterable =
                    props.filterableHeaderIds.includes(header.id) ||
                    (props.renderHeaderFilter && header.column.getCanFilter())

                const batchHeaderControl = props.isBatchMode
                    ? props.renderBatchHeaderControl?.(header, props.tableInit)
                    : null

                const hasCustomHeaderFilter = Boolean(props.renderHeaderFilter && header.column.getCanFilter())
                const hasSearchVisibilityOverride = Object.prototype.hasOwnProperty.call(
                    props.searchVisibility,
                    header.id
                )
                const isSearchVisible =
                    columnFiltersVisible &&
                    (hasCustomHeaderFilter || (hasSearchVisibilityOverride ? props.searchVisibility[header.id] : true))

                const columnMeta = header.column.columnDef.meta as TableColumnMeta | undefined
                const resolvedWidthKey = resolveTableColumnWidthKey(header.id, columnMeta)
                const isNarrowColumn =
                    resolvedWidthKey === 'id' || resolvedWidthKey === 'actionsCompact' || resolvedWidthKey === 'icon'
                const isIdColumn = header.id === 'id'

                const isActionsColumn = header.id === 'actions'
                const isSelectColumn = header.id === 'select'

                const filterSlotContent = hasCustomHeaderFilter ? (
                    props.renderHeaderFilter?.(header, props.tableInit)
                ) : isSearchVisible ? (
                    <UiTableColumnSearch
                        value={(header.column.getFilterValue() as string) ?? ''}
                        onChange={(value) => header.column.setFilterValue(value || undefined)}
                        placeholder={isIdColumn ? props.translate('ID') : props.translate('Search...')}
                    />
                ) : (
                    <div className={clsx('h-8 w-full', isNarrowColumn ? 'min-w-0' : 'min-w-25')} aria-hidden="true" />
                )

                return (
                    <TableHeadColumn
                        key={header.id}
                        colSpan={header.colSpan}
                        className={clsx(
                            getTableColumnClassName(columnMeta, header.id),
                            (isActionsColumn || isSelectColumn) && 'overflow-visible'
                        )}
                    >
                        {header.isPlaceholder ? null : (
                            <TableHeadColumnInnerWrapper
                                className={clsx(
                                    isSelectColumn && 'justify-center',
                                    isActionsColumn && 'items-end overflow-visible'
                                )}
                            >
                                <TableHeadColumnTitle
                                    className={clsx(
                                        'leading-tight',
                                        isSelectColumn && 'h-full justify-center flex-nowrap',
                                        isActionsColumn && 'justify-end items-center h-full flex-nowrap shrink-0',
                                        !isActionsColumn && !isSelectColumn && 'justify-start items-center min-w-0'
                                    )}
                                    header={header}
                                    tableInit={props.tableInit}
                                    showSearchToggle={columnFiltersVisible && isFilterable && !hasCustomHeaderFilter}
                                    isSearchVisible={isSearchVisible}
                                    onToggleSearch={() => props.onToggleSearch(header.id)}
                                    {...(props.renderHeaderTitleProps
                                        ? props.renderHeaderTitleProps(header, props.tableInit)
                                        : {})}
                                >
                                    {batchHeaderControl}
                                </TableHeadColumnTitle>
                                {isFilterable && (
                                    <div
                                        className={clsx(
                                            'w-full min-w-0',
                                            columnFiltersVisible
                                                ? 'min-h-8'
                                                : 'h-0 min-h-0 overflow-hidden invisible pointer-events-none'
                                        )}
                                        aria-hidden={!columnFiltersVisible}
                                    >
                                        {columnFiltersVisible ? (
                                            filterSlotContent
                                        ) : (
                                            <div
                                                className={clsx('h-8 w-full', isNarrowColumn ? 'min-w-0' : 'min-w-25')}
                                                aria-hidden="true"
                                            />
                                        )}
                                    </div>
                                )}
                            </TableHeadColumnInnerWrapper>
                        )}
                    </TableHeadColumn>
                )
            })}
        </TableHeadRow>
    )
}

export default UiTanstackTableHeaderRow
